// The palette's pure contract: the row/group shapes, the two constants that mirror the backend, and
// the aria-live wording. Deliberately free of imports.
//
// This is split from useCommandPalette on purpose. That module imports GlobalSearchService, which
// reaches msalClient, which touches `window` at module scope - so anything importing the hook cannot
// be unit-tested in the node environment this suite runs in. Keeping the pure half here makes the
// testable part testable without a jsdom shim or a mocked MSAL.

// Minimum characters accepted. This is SUBMIT VALIDATION, not a fire threshold: search is
// submit-driven, so a short query is rejected outright rather than debounced away.
// Mirrors Tp_Communication GlobalSearchConstants.MinQueryLength.
export const MIN_QUERY_LENGTH = 2;

// Rows per entity type. Mirrors GlobalSearchConstants.DefaultTopN; the server clamps anyway.
export const DEFAULT_TOP_N = 8;

export interface GlobalSearchHit {
  id: number;
  title: string;
  subTitle?: string | null;
  detailUrl: string;
}

export interface GlobalSearchGroup {
  entityType: string;
  label: string;
  // Items.length, NOT a true total - the server runs no CountAsync per type. When it equals the
  // requested topN the UI shows "8+" and a refine hint rather than a number.
  count: number;
  items: GlobalSearchHit[];
}

export interface GlobalSearchResult {
  groups: GlobalSearchGroup[];
  resolvedConcepts?: string[] | null;
  effectiveQuery?: string | null;
  // Labels of the types the search would have covered but the caller's roles forbid. Populated ONLY
  // when those types were explicitly asked for (chips, or a "Shop: 1234" prefix) - see
  // GlobalSearchResponse.UnavailableGroups for why an unscoped search stays quiet.
  unavailableGroups?: string[] | null;
  // How many types were actually queried. At zero, "no records match" would be a claim about data
  // nobody looked at.
  searchedTypeCount?: number;
}

export interface GlobalSearchConcept {
  key: string;
  entityTypes: string[];
  // The concept the palette starts scoped to, decided by the vocabulary's ordering rather than by a
  // name hardcoded here - see GlobalSearchConceptResource.
  isDefault?: boolean;
}

export interface StatusMessageState {
  isSearching: boolean;
  errorMessage?: string | null;
  validationMessage?: string | null;
  submittedQuery?: string | null;
  hitCount: number;
  groups?: GlobalSearchGroup[] | null;
  unavailableGroups?: string[] | null;
  searchedTypeCount?: number;
}

/** How many remembered searches the palette SHOWS. Short on purpose: a long list is a second search
 *  problem. */
export const RECENT_SEARCH_LIMIT = 5;

/**
 * How many it KEEPS. Far more than it shows, because the history is not only a convenience list any more —
 * it is the raw material for working out which searches a person actually relies on.
 *
 * The entries are per DISTINCT search, not per event, so 100 is a lot of history rather than a little: the
 * same search repeated fifty times is one row with a count of fifty. That is the whole reason for storing
 * counters instead of a longer raw list — the same information in roughly the space the five raw entries
 * used to take.
 */
export const RECENT_SEARCH_HISTORY_LIMIT = 100;

/** One remembered search — the text and the scope it ran under, since the same text under a different
 *  scope is a different question and re-running it without the scope would answer the wrong one. */
export interface RecentSearch {
  query: string;
  concepts: string[];
  /** How many times this exact search has been run. Absent on entries written before counters existed. */
  count?: number;
  /** ISO timestamp of the most recent run. Absent on pre-counter entries. */
  lastUsedAt?: string;
}

function keyOf(entry: RecentSearch): string {
  const concepts = [...(entry.concepts || [])].sort().join(',').toLowerCase();

  return `${(entry.query || '').trim().toLowerCase()}::${concepts}`;
}

/**
 * Adds or updates a search, newest first, incrementing its counter.
 *
 * Matching is on text AND scope: "smith" scoped to Evaluator and "smith" scoped to everything are two
 * different searches, and collapsing them would silently re-run the wrong one. Case-insensitive on the
 * text, because retyping it with different capitalisation is the same search to a person.
 *
 * `now` is injected rather than read from the clock so the ordering rules can be tested without waiting.
 */
export function addRecentSearch(
  list: RecentSearch[],
  entry: RecentSearch,
  limit: number = RECENT_SEARCH_HISTORY_LIMIT,
  now: Date = new Date()
): RecentSearch[] {
  const query = (entry.query || '').trim();

  if (!query) return list;

  const concepts = [...(entry.concepts || [])].sort();
  const key = keyOf({ query, concepts });
  const existing = list.find((item) => keyOf(item) === key);

  const updated: RecentSearch = {
    query,
    concepts,
    count: (existing?.count || 0) + 1,
    lastUsedAt: now.toISOString(),
  };

  const rest = list.filter((item) => keyOf(item) !== key);

  return capHistory([updated, ...rest], limit);
}

/**
 * Trims the history to the cap by dropping the LEAST USED entries, not the oldest.
 *
 * Dropping the oldest would be simpler and would quietly defeat the point: a search someone runs every
 * week would fall out of the store after a hundred one-off lookups, which is precisely the search worth
 * remembering. Ties break on age, so of two equally-used entries the staler one goes.
 */
export function capHistory(list: RecentSearch[], limit: number = RECENT_SEARCH_HISTORY_LIMIT): RecentSearch[] {
  if (list.length <= limit) return list;

  const ranked = [...list].sort((a, b) => {
    const byCount = (b.count || 1) - (a.count || 1);

    if (byCount !== 0) return byCount;

    return (b.lastUsedAt || '').localeCompare(a.lastUsedAt || '');
  });

  const keep = new Set(ranked.slice(0, limit).map(keyOf));

  // Filtering the ORIGINAL list keeps it in recency order; only membership is decided by the ranking.
  return list.filter((item) => keep.has(keyOf(item)));
}

/** The rows the palette shows: the most recent, which is what a "recent searches" list must mean. */
export function recentSearchesForDisplay(list: RecentSearch[], limit: number = RECENT_SEARCH_LIMIT): RecentSearch[] {
  return list.slice(0, Math.max(0, limit));
}

/**
 * Reads the stored list back, defensively. Anything unrecognised yields an empty list rather than
 * throwing: this is a convenience, and a corrupted entry must never be able to stop the palette from
 * opening.
 *
 * Entries written before counters existed are kept and given a count of 1 rather than discarded — there is
 * no reason to throw away someone's history to change its shape.
 */
export function parseRecentSearches(raw: string | null | undefined): RecentSearch[] {
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw);

    if (!Array.isArray(parsed)) return [];

    return parsed
      .filter((item) => item && typeof item.query === 'string' && !!item.query.trim())
      .map((item) => ({
        query: item.query.trim(),
        concepts: Array.isArray(item.concepts) ? item.concepts.filter((c: unknown) => typeof c === 'string') : [],
        count: typeof item.count === 'number' && item.count > 0 ? item.count : 1,
        lastUsedAt: typeof item.lastUsedAt === 'string' ? item.lastUsedAt : undefined,
      }))
      .slice(0, RECENT_SEARCH_HISTORY_LIMIT);
  } catch (error) {
    return [];
  }
}

/**
 * Whether the results on screen belong to what is currently in the search box — which is what decides
 * whether Enter OPENS the highlighted hit or submits a new search.
 *
 * The subtlety is the consumed prefix. Searching "Client: tucson" submits that whole string, but the
 * prefix is then stripped from the input, leaving "tucson". Comparing the box against the submitted text
 * alone therefore said "these results are stale" for every prefixed search, and Enter re-ran the search
 * instead of opening the selected record - breaking arrow-keys-then-Enter, the main keyboard path,
 * exactly when scoping was used. Compare against the EFFECTIVE query when there is one.
 */
export function resultsMatchCurrentQuery(
  currentQuery: string,
  submittedQuery: string | null | undefined,
  effectiveQuery: string | null | undefined
): boolean {
  const current = (currentQuery || '').trim();

  if (!current) return false;

  const searched = (effectiveQuery || submittedQuery || '').trim();

  return searched === current;
}

/**
 * The chip selection after clicking `key`, given what was selected before.
 *
 * Two behaviours, the desktop idiom from every file list: a plain click means "just this one", and a
 * ctrl/cmd-click adds or removes. It started as toggle-only, which was right with three chips and wrong
 * with eight — narrowing to one concept, the common case, cost two clicks and left whatever was lit
 * before still lit.
 *
 * The corner that needs stating: clicking the chip that is ALREADY the only one selected clears it, so
 * "search everything" stays reachable with a plain click. Without that rule, strict exclusive-select
 * makes an empty selection unreachable unless you know about ctrl — and an unscoped search is not an
 * exotic thing to want.
 */
export function nextConceptSelection(current: string[], key: string, additive: boolean): string[] {
  if (additive) {
    return current.includes(key) ? current.filter((existing) => existing !== key) : current.concat(key);
  }

  const alreadyOnlyThis = current.length === 1 && current[0] === key;

  return alreadyOnlyThis ? [] : [key];
}

/**
 * Whether clicking a chip should re-run the search straight away.
 *
 * A plain click does once something has been searched: it means "show me this kind instead", like a tab,
 * and leaving the previous result up made the panel describe a search that never ran ("No records match …
 * in Client" while the Evaluator result was still showing, 2026-10-21). A ctrl/cmd-click does not: it builds
 * a selection of several kinds, and searching on every click of that would fire one search per chip. Before
 * the first search a click only sets the scope, as there is nothing to show yet.
 */
export function shouldSearchOnConceptPick(additive: boolean, submittedQuery: string): boolean {
  return !additive && submittedQuery.length > 0;
}

/**
 * The union of the picked concepts' entity types, or null for "everything I may see". Null rather than an
 * empty array matters: the service reads null as "the caller did not scope", whereas an empty list would be
 * an explicit request for nothing.
 */
export function entityTypesForConcepts(concepts: GlobalSearchConcept[], conceptKeys: string[]): string[] | null {
  if (conceptKeys.length === 0) {
    return null;
  }

  return concepts
    .filter((concept) => conceptKeys.includes(concept.key))
    .reduce((all: string[], concept) => all.concat(concept.entityTypes || []), []);
}

/** The localStorage key recent searches are kept under, or null when there is no user to key them to. */
export const RECENT_SEARCH_KEY_PREFIX = 'bravo.recordSearch.recent';

/**
 * Recent searches are stored PER USER, not per browser.
 *
 * localStorage is shared by everyone using a browser profile, and Bravo holds several accounts at once
 * (AuthService keeps a `loggedInUsers` array with an `inUse` flag), so an unkeyed list showed one
 * person's remembered client names and invoice numbers to the next. The results were always
 * permission-scoped - re-running goes through the gate like any search - but the remembered STRINGS were
 * not, and they outlived a logout.
 *
 * Returns null when the user is unknown, and callers then neither read nor write. Failing closed matters
 * more than the convenience: a shared fallback key is the exact thing being fixed.
 */
export function recentSearchStorageKey(userId: number | string | null | undefined): string | null {
  if (userId === null || userId === undefined || `${userId}`.trim() === '') return null;

  return `${RECENT_SEARCH_KEY_PREFIX}.${userId}`;
}

/** How a remembered search reads in the list: the text, plus its scope when it had one. */
export function describeRecentSearch(entry: RecentSearch): string {
  const concepts = (entry.concepts || []).filter((concept) => !!concept);

  return concepts.length === 0 ? entry.query : `${entry.query} — ${concepts.join(', ')}`;
}

/**
 * The chip selection implied by a search that resolved a "Concept:" prefix, so the two ways of scoping
 * end up as ONE state instead of two that can disagree.
 *
 * The prefix is authoritative here because the server already searched by it (see
 * GlobalSearchService.ResolveEntityTypes) - this only makes the panel say what actually happened. It was
 * the disagreement that was the bug: the Evaluator chip lit, "Client:" typed, Client searched, and every
 * label on screen claiming something different.
 *
 * Concepts the caller cannot search are dropped, because a lit chip that is not in the row would leave
 * the scope summary naming something the user cannot see or unset. When that empties the selection the
 * result is no chips - correct, since nothing was searchable, and the unavailable-scope notice explains
 * why.
 *
 * Returns null when there is nothing to sync, so a caller can skip a pointless state update.
 */
export function conceptsFromResolvedScope(
  resolvedConcepts: string[] | null | undefined,
  availableConceptKeys: string[]
): string[] | null {
  const resolved = (resolvedConcepts || []).filter((concept) => !!concept);

  if (resolved.length === 0) return null;

  const available = availableConceptKeys.map((key) => key.toLowerCase());

  return resolved.filter((concept) => available.indexOf(concept.toLowerCase()) !== -1);
}

/** "a", "a and b", "a, b and c" - so the notice reads as a sentence rather than a comma list. */
export function formatLabelList(labels: string[]): string {
  if (labels.length <= 1) return labels[0] || '';
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
}

/**
 * The "…was not searched, you do not have access" sentence, or '' when there is nothing to disclose.
 *
 * Shown wherever a scope was refused, because the alternative is the palette reporting "no records
 * match" for data it never looked at - which reads as "this record does not exist" and is how a user
 * ends up trusting a false negative.
 */
export function buildUnavailableNotice(unavailableGroups?: string[] | null): string {
  const labels = (unavailableGroups || []).filter((label) => !!label);

  if (labels.length === 0) return '';

  return `${formatLabelList(labels)} ${labels.length === 1 ? 'was' : 'were'} not searched — you do not have access.`;
}

/**
 * True when the caller's whole scope was refused, so nothing was queried at all. The wording has to
 * change completely here: any "no records match" phrasing would be a claim about data nobody read.
 *
 * `searchedTypeCount` is checked against 0 explicitly rather than falsily - an older response without
 * the field must fall through to the ordinary wording, not into this branch.
 */
export function isScopeFullyUnavailable(state: {
  unavailableGroups?: string[] | null;
  searchedTypeCount?: number;
}): boolean {
  return state.searchedTypeCount === 0 && (state.unavailableGroups || []).length > 0;
}

/**
 * What the palette's aria-live region announces. Results otherwise replace the panel's contents in
 * silence: a screen-reader user presses Enter and hears nothing, unable to tell a slow search from
 * an empty one.
 *
 * The order of the branches is the point - a stale count spoken while the next search is in flight
 * is worse than no count at all, so "Searching…" outranks everything.
 */
export function buildStatusMessage(state: StatusMessageState): string {
  if (state.isSearching) return 'Searching…';
  if (state.errorMessage) return state.errorMessage;
  if (state.validationMessage) return state.validationMessage;
  if (!state.submittedQuery) return '';

  const notice = buildUnavailableNotice(state.unavailableGroups);

  // Nothing was queried, so say only that. Appending it to "no records match" would still leave the
  // false claim standing, and it is the claim - not its absence - that misleads.
  if (isScopeFullyUnavailable(state)) return notice;

  const suffix = notice ? ` ${notice}` : '';

  if (state.hitCount === 0) return `No records match ${state.submittedQuery}.${suffix}`;

  const groupSummary = (state.groups || [])
    .map((group) => `${group.count >= DEFAULT_TOP_N ? `${DEFAULT_TOP_N} or more` : group.count} in ${group.label}`)
    .join(', ');

  return `${state.hitCount} result${
    state.hitCount === 1 ? '' : 's'
  }: ${groupSummary}. Use the arrow keys to review them.${suffix}`;
}
